/**
 * DriverScorecardPage — `/driver-portal/scorecard`.
 *
 * Wired to:
 *   - GET /v1/driver-portal/me/scorecard       (on-time, stops, last-30d)
 *   - GET /v1/driver-portal/me/active-shift    (distance / day, last 24h)
 *   - GET /v1/driver-portal/me/safety-stats    (accident-free streak)
 *
 * Chart previews (weekly bars + failure-reason breakdown) remain as styled
 * placeholders per Phase 7c.
 */
import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Award,
  CheckCircle2,
  Clock,
  MapPin,
  Star,
  Trophy,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getDriverPortalActiveShift,
  getDriverPortalSafetyStats,
  getDriverPortalScorecard,
} from '../../lib/api/driver-portal';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { useDriverPortalContext } from './DriverPortalLayout';

export function DriverScorecardPage(): JSX.Element {
  const { driverId } = useDriverPortalContext();

  const scorecard = useQuery({
    queryKey: ['driver-portal', 'scorecard', driverId],
    queryFn: () => getDriverPortalScorecard(driverId),
    retry: false,
    staleTime: 60_000,
  });

  const activeShift = useQuery({
    queryKey: ['driver-portal', 'active-shift', driverId],
    queryFn: () => getDriverPortalActiveShift(driverId),
    retry: false,
    staleTime: 60_000,
  });

  const safetyStats = useQuery({
    queryKey: ['driver-portal', 'safety-stats', driverId],
    queryFn: () => getDriverPortalSafetyStats(driverId),
    retry: false,
    staleTime: 60_000,
  });

  const denied =
    scorecard.isError &&
    scorecard.error instanceof ApiError &&
    (scorecard.error.status === 401 || scorecard.error.status === 403);

  const data = scorecard.data ?? null;
  const shiftData = activeShift.data ?? null;
  const safety = safetyStats.data ?? null;

  function fmt(val: number | null | undefined): string {
    if (val == null) return '—';
    return String(val);
  }

  function fmtPct(val: number | null | undefined): string {
    if (val == null) return '—';
    return `${val}%`;
  }

  const distancePerDayLabel = shiftData
    ? `${(shiftData.distanceMetersLast24h / 1000).toFixed(1)} km`
    : '—';
  const accidentFreeLabel =
    safety?.accidentFreeDays != null
      ? `${safety.accidentFreeDays}d`
      : '—';

  return (
    <div>
      <PageHeader
        title="My scorecard"
        description="Last 30 days — based on completed deliveries."
      />

      {scorecard.isPending ? (
        <p className="text-sm text-muted my-6">Loading scorecard…</p>
      ) : denied ? (
        <p className="text-sm text-destructive my-6">
          Permission required: Driver/readScorecard
        </p>
      ) : scorecard.isError ? (
        <p className="text-sm text-destructive my-6">
          {(scorecard.error as Error).message}
        </p>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6 mt-4" data-testid="scorecard-kpis">
        <KPICard
          title="Stops attempted"
          value={fmt(data?.stopsAttempted)}
          hint="Last 30 days"
          icon={CheckCircle2}
          tone={data ? 'default' : 'warning'}
        />
        <KPICard
          title="Stops delivered"
          value={fmt(data?.stopsDelivered)}
          hint="Last 30 days"
          icon={Clock}
          tone={data ? 'default' : 'warning'}
        />
        <KPICard
          title="On-time %"
          value={fmtPct(data?.onTimeRate)}
          hint="Delivered / Attempted"
          icon={Award}
          tone={data ? 'default' : 'warning'}
        />
        <KPICard
          title="First-attempt success"
          value={fmtPct(data?.firstAttemptSuccessRate)}
          hint="Coming soon"
          icon={Star}
          tone={data ? 'default' : 'warning'}
        />
        <KPICard
          title="Distance / day"
          value={distancePerDayLabel}
          hint="active-shift.distanceMetersLast24h"
          icon={MapPin}
          tone={shiftData ? 'default' : 'warning'}
        />
        <KPICard
          title="Accident-free streak"
          value={accidentFreeLabel}
          hint="Days since your last reported incident"
          icon={Activity}
          tone={safety ? 'success' : 'warning'}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Weekly breakdown */}
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-nipost-gold" /> This week&apos;s performance
          </h3>
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-sm font-semibold text-gray-700">Chart preview — coming soon</p>
            <p className="text-xs text-muted mt-1">
              Weekly stops / on-time % / rating bars will render here.
            </p>
          </div>
        </div>

        {/* Failure reasons */}
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">
            Failure reasons (this month)
          </h3>
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center">
            <p className="text-sm font-semibold text-gray-700">Chart preview — coming soon</p>
            <p className="text-xs text-muted mt-1">
              Stop failure reason breakdown will render here.
            </p>
          </div>
          <p className="text-[11px] text-muted mt-4">
            Customer ratings are aggregated across the month and never shown to
            you on a per-customer basis (anti-harassment).
          </p>
        </div>
      </div>
    </div>
  );
}
